import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Flashbar,
  Modal,
  Pagination,
  PropertyFilter,
  Table,
  TableProps,
} from '@amzn/awsui-components-react-v3/polaris';
import { listDataSets } from 'src/api/catalog';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { editDataLakeRole, listDataLakeRoleProperty } from 'src/api/permissions';
import { defaultWrapLinesPreference, largePageSizePreference, paginationLabels } from 'src/commons/tables';
import { compareBy } from 'src/components/utils/sorting';
import {
  DATA_LOAD_LIMIT,
  DZ_REDSHIFT_DATASOURCE_ID,
  IAM_ACCESS_ITEM,
  REDSHIFT_DATASOURCE_ID,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { createCatalogDetailLink, createDatabaseDetailLink, createDatasetDetailLink } from 'src/routes';
import { getHCResourcePagePropertyFilter, isSearchEnabled, keyForCatalogNameMap } from 'src/commons/common';
import { ownerLinkItem } from 'src/components/workspaces/common/common';
import { activeGroupOrWorkspaceId } from 'src/components/dataadvisory/constants';
import { EmptyState } from 'src/commons/EmptyState';
import { CopyToClipboard, Header, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { PageHeader } from 'src/components/common/PageHeader';
import { Link } from 'src/components/common/Link';
import { CartAccessItem } from 'src/components/common/checkout/common';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface BrowseTableProps extends CommonPageProps {
  idFilter?: string[];
  extraFeatures: boolean;
  onlyShowDatasetName?: boolean;
  title: string;
  loading?: boolean;
  roleArns?: string[];
  iamDatasetPermission?: boolean;
  multiSelection?: boolean;
  disableFiltering?: boolean;
}

const findRoleArn = (item, roleArns) => {
  if (roleArns == null) return null;

  for (const roleArn of roleArns) {
    if (roleArn.split(':')[4] == item.IdInfo.CatalogId) {
      return roleArn;
    }
  }
};

export const flattenItem = (item, roleArns, catalog) => {
  return {
    dataSetName: item['DataSetName'],
    clusterId: item['IdInfo']['ClusterIdentifier'],
    redshiftWorkgroupName: item['IdInfo']['RedshiftWorkgroupName'],
    bucketName: item['IdInfo']['BucketName'],
    databaseName: item['IdInfo']['DatabaseName'],
    tableName: item['IdInfo']['TableName'],
    dataSourceId: item['IdInfo']['DataSourceId'],
    catalogId: item['IdInfo']['CatalogId'],
    region: item['IdInfo']['Region'],
    catalogInfo: item['catalogInfo'] || catalog,
    catalogDisplayName: item['catalogDisplayName'],
    id: item['Id'],
    roleArn: findRoleArn(item, roleArns),
    supportedAccessTypes: item['SupportedAccessTypes'],
    owners: item['Owners'],
    dataClassificationOption: item['DataClassification'] === 'Private' ? item['DataClassification'] : 'Public',
    primaryOwner: item['PrimaryOwner'],
  };
};

const BrowseTable = (props: BrowseTableProps) => {
  const [allItems, setItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [roleProperties, setRoleProperties] = useState(undefined);
  const [modalVisible, setModalVisible] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [paginationDisabled, setPaginationDisabled] = useState(false);
  const [nextToken, setNextToken] = useState<string>(null);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  const [columnDefinitions, setColumnDefinitions] = useState<TableProps.ColumnDefinition<any>[]>([
    {
      id: 'tableName',
      header: 'Table name',
      cell: (item) => <Link href={createDatasetDetailLink(item?.id)}>{item.tableName}</Link>,
      minWidth: 200,
      sortingField: 'tableName',
    },
    {
      id: 'databaseName',
      header: 'Database name',
      cell: (item) => (
        <Link
          href={createDatabaseDetailLink(
            item?.dataSourceId,
            item?.catalogId,
            undefined,
            undefined,
            item?.bucketName,
            item?.databaseName,
            item?.region,
          )}
        >
          {item.databaseName}
        </Link>
      ),
      minWidth: 200,
      sortingField: 'databaseName',
      sortingComparator: compareBy('databaseName', 'tableName'),
    },
    {
      id: 'catalogDisplayName',
      header: 'Catalog name',
      cell: (item) => (
        <Link
          href={createCatalogDetailLink(
            item?.dataSourceId,
            item?.catalogId,
            item?.clusterId,
            item?.redshiftWorkgroupName,
            item?.bucketName,
            item?.region,
          )}
        >
          {item.catalogDisplayName}
        </Link>
      ),
      minWidth: 100,
      sortingField: 'catalogDisplayName',
      sortingComparator: compareBy('catalogDisplayName', 'databaseName', 'tableName'),
    },
    {
      id: 'owner',
      header: 'Owner',
      cell: (item) => ownerLinkItem(item.primaryOwner, props.workspaceNameMap),
      minWidth: 200,
      sortingField: 'owner',
    },
  ]);

  // Load role properties.  Currently used to indicate that some items
  // can't be added to cart because they are pending permissions or
  // have already approved permissions.  Note that the active group

  // simplifies the dataset objects in order to make the rest of the code a bit cleaner
  const flattenItems = (items, roleArns): CartAccessItem[] => {
    return items.map((item) => ({
      ...flattenItem(item, roleArns, null),
      catalogDisplayName: getCatalogName(item),
      catalogName: getCatalogName(item),
      // other required items for cart checkouts
      datasetName: item['DataSetName'],
      owner: item['PrimaryOwner'],
      itemType: IAM_ACCESS_ITEM,
      dataOwnerRole: undefined,
      dataLakePrincipal: undefined,
      permissionId: undefined,
    }));
  };

  // Centralized filtering logic for datasets
  const applyFiltering = (dataSetList) => {
    // Filter 1: Remove Redshift datasets
    let filtered = dataSetList.filter(
      (item) => ![REDSHIFT_DATASOURCE_ID, DZ_REDSHIFT_DATASOURCE_ID].includes(item.DataSourceType),
    );

    // Filter 2: Remove private datasets user can't see
    let nonPrivateDataSets = [];
    let privateDataSets = [];

    for (const dataSet of filtered) {
      if (dataSet.DataClassification === undefined || dataSet.DataClassification != 'Private') {
        nonPrivateDataSets.push(dataSet);
      } else {
        if (dataSet.Owners.includes(activeGroupOrWorkspaceId(props))) {
          privateDataSets.push(dataSet);
        }
      }

      // Apply valid Primary owner to datasets without one so it can be filterable
      dataSet.PrimaryOwner = dataSet.PrimaryOwner != null ? dataSet.PrimaryOwner : 'No primary owner';
    }

    const datasets = nonPrivateDataSets.concat(privateDataSets);
    return flattenItems(datasets, props.roleArns);
  };

  const getCatalogName = (item: any) => {
    return (
      props.catalogMap.get(
        keyForCatalogNameMap({
          dataSource: item.IdInfo?.DataSourceId,
          catalogId: item.IdInfo?.CatalogId,
          clusterId: item.IdInfo?.ClusterIdentifier,
          redshiftWorkgroupName: item.IdInfo?.RedshiftWorkgroupName,
          bucketName: item.IdInfo?.BucketName,
          region: item.IdInfo?.Region,
        }),
      ) ?? item.IdInfo?.CatalogId
    );
  };

  // is an optional props
  const loadRoleProperties = async () => {
    const roleProperties = await listDataLakeRoleProperty({
      groupId: props.activeGroup,
    });

    setRoleProperties(roleProperties);
  };

  const componentDidMount = async () => {
    if (props.onlyShowDatasetName) {
      setColumnDefinitions([
        {
          id: 'dataSetName',
          header: 'Table name',
          cell: (item) => <Link href={createDatasetDetailLink(item?.id)}>{item.tableName}</Link>,
          minWidth: 200,
        },
      ]);
    }

    props.setContentType(TABLE_CONTENT_TYPE);
    if (props.roleArns != null) {
      columnDefinitions.push({
        id: 'roleArn',
        header: 'Role ARN',
        cell: (item) => (
          <CopyToClipboard
            textToCopy={item.roleArn}
            textToDisplay={item.roleArn}
            copySuccessText={'Successfully copied role ARN'}
            copyErrorText={'Failed to copy role ARN'}
            variant={'inline'}
          />
        ),
        minWidth: 200,
      });
    }

    setLoading(true);
    await fetchAllData();
    if (props.extraFeatures && props.activeGroup) await loadRoleProperties();
    setLoading(false);
  };

  useEffect(() => {
    componentDidMount();
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [props.idFilter]);

  useEffect(() => {
    if (props.extraFeatures && props.activeGroup) loadRoleProperties();
  }, [props.activeGroup]);

  // Fetch datasets and then their catalogs
  const fetchAllData = async () => {
    setLoading(true);
    await fetchDatasets();
    setLoading(false);
  };

  const fetchDatasets = async () => {
    let dataSets;
    let dataSetList = [];
    try {
      if (props.idFilter) {
        // Load all filtered IDs (existing behavior)
        for (let index = 0; index < props.idFilter.length; index += DATA_LOAD_LIMIT) {
          let request = {
            Filter: { IdList: props.idFilter.slice(index, index + DATA_LOAD_LIMIT) },
            Limit: DATA_LOAD_LIMIT,
            IncludeTotalCount: true,
          };
          dataSets = await listDataSets(request);
          let currentList = applyFiltering(dataSets.DataSetList);
          dataSetList.push(...currentList);
        }
        setNextToken(null);
        setAllDataLoaded(true);
        setTotalCount(props.idFilter.length);
      } else {
        // Load batches until we have enough items or run out of data
        let currentNextToken = null;
        let hasMoreData = true;
        let isFirstBatch = true;

        while (dataSetList.length < preferences.pageSize && hasMoreData) {
          let request = {
            Limit: DATA_LOAD_LIMIT,
            NextToken: currentNextToken,
            IncludeTotalCount: isFirstBatch,
          };
          dataSets = await listDataSets(request);

          // Set total count from first batch
          if (isFirstBatch && dataSets.TotalCount) {
            setTotalCount(dataSets.TotalCount);
            isFirstBatch = false;
          }

          // Apply filtering and accumulate
          const filteredItems = applyFiltering(dataSets.DataSetList);
          dataSetList.push(...filteredItems);

          // Update pagination state
          currentNextToken = dataSets.NextToken;
          hasMoreData = currentNextToken != null;
        }

        setNextToken(currentNextToken);
        setAllDataLoaded(!hasMoreData);
      }
      setItems(dataSetList);
    } catch (e) {
      console.log('Exception when fetch datasets', e);
      setItems(dataSetList);
      setAllDataLoaded(true);
      setNotifications([
        {
          type: 'error',
          content: `Error when trying to fetch datasets: ${e.message}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  const loadMoreData = async () => {
    if (props.idFilter) {
      return;
    }
    setPaginationDisabled(true);
    let request = { Limit: DATA_LOAD_LIMIT, NextToken: nextToken };
    let dataSets = await listDataSets(request);

    setNextToken(dataSets.NextToken);
    const flatItems = applyFiltering(dataSets.DataSetList);
    let currentItems = allItems;
    currentItems.push(...flatItems);
    setItems(currentItems);
    if (dataSets.NextToken == null) {
      setAllDataLoaded(true);
    }
    setPaginationDisabled(false);
  };

  const handleIamDatasetsPermissionActions = async (e) => {
    if (e.detail.id === 'relinquish') openModal();
  };

  const handleRelinquishPermission = async () => {
    setButtonLoading(true);

    const datasetsId = [];
    for (var i = 0; i < selected.length; i++) {
      datasetsId.push(selected[i].id);
    }

    await editDataLakeRole({
      groupId: props.activeGroup,
      datasetsToDelete: datasetsId,
    });

    setSelected([]);

    for (let i = 0; i < datasetsId.length; i++) {
      props.idFilter.splice(props.idFilter.indexOf(datasetsId[i]), 1);
    } // Potential bug: datasets are spliced on the front-end, regardless of the response.

    closeModal();

    setLoading(true);
    await fetchDatasets();
    setLoading(false);
  };

  const openModal = () => {
    setModalVisible(true);
  };

  const closeModal = () => {
    setButtonLoading(false);
    setModalVisible(false);
  };

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(allItems, {
    filtering: {},
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: {
          sortingField: 'catalogDisplayName',
          sortingComparator: compareBy('catalogDisplayName', 'databaseName', 'tableName'),
        },
      },
    },
    selection: {},
    propertyFiltering: {
      noMatch: {}, // filtering is disabled
      empty: <EmptyState title='No datasets' subtitle='No datasets were found in the catalog.' />,
      filteringProperties: [
        {
          propertyLabel: 'Catalog name',
          key: 'catalogDisplayName',
          groupValuesLabel: 'Catalog names',
        },
        {
          propertyLabel: 'Database',
          key: 'databaseName',
          groupValuesLabel: 'Databases',
        },
        {
          propertyLabel: 'Table name',
          key: 'tableName',
          groupValuesLabel: 'Table names',
        },
        {
          propertyLabel: 'Data visibility',
          key: 'dataClassificationOption',
          groupValuesLabel: 'Data visibility options',
        },
        {
          propertyLabel: 'Primary owner',
          key: 'primaryOwner',
          groupValuesLabel: 'Primary owners',
        },
      ],
    },
  });

  const handlePageChange = async (e) => {
    const targetPageIndex = e.detail.currentPageIndex;
    const requiredItems = targetPageIndex * preferences.pageSize;

    // Check if we need more data for the target page
    if (requiredItems > allItems.length && !allDataLoaded) {
      setPaginationDisabled(true);

      try {
        // Load batches until we have enough for target page
        let currentItems = [...allItems];
        let currentNextToken = nextToken;
        let hasMoreData = currentNextToken != null;

        while (currentItems.length < requiredItems && hasMoreData) {
          const request = { Limit: DATA_LOAD_LIMIT, NextToken: currentNextToken };
          const dataSets = await listDataSets(request);

          const filteredItems = applyFiltering(dataSets.DataSetList);
          currentItems.push(...filteredItems);
          currentNextToken = dataSets.NextToken;
          hasMoreData = currentNextToken != null;
        }

        // Update state
        setItems(currentItems);
        setNextToken(currentNextToken);
        setAllDataLoaded(!hasMoreData);
      } catch (e) {
        // Error: show notification, keep existing items
        console.log('Exception when loading more datasets', e);
        setNotifications([
          {
            type: 'error',
            content: `Error when trying to fetch datasets: ${e.message}`,
            dismissible: true,
            onDismiss: () => setNotifications([]),
          },
        ]);
        setAllDataLoaded(true);
      } finally {
        setPaginationDisabled(false);
      }
    }

    // Always call pagination onChange to update UI
    paginationProps.onChange(e);
  };

  return (
    <>
      <Flashbar items={notifications} />
      <Table
        {...collectionProps}
        loadingText='Loading datasets...'
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        trackBy={'id'}
        selectedItems={selected}
        onSelectionChange={({ detail }) => setSelected(detail.selectedItems)}
        header={
          <>
            <PageHeader
              buttons={
                props.extraFeatures && props.addToCart
                  ? [
                      {
                        text: 'Add to cart',
                        variant: 'normal',
                        disabled: selected.length === 0,
                        onItemClick: () => {
                          props.addToCart(selected);
                          setSelected([]);
                        },
                      },
                    ]
                  : props.iamDatasetPermission
                  ? [
                      {
                        text: 'Actions',
                        onItemClick: handleIamDatasetsPermissionActions,
                        items: [
                          {
                            text: 'Relinquish permission',
                            id: 'relinquish',
                            disabled: !selected.length,
                          },
                        ],
                        loading: isLoading,
                      },
                    ]
                  : []
              }
              counter={`(${totalCount}${!allDataLoaded && props.idFilter ? '+' : ''})`}
              header={props.title}
            />

            <Modal
              visible={modalVisible}
              header={[`Are you sure to relinquish the permission for datasets below?`]}
              onDismiss={closeModal}
              footer={
                <Header
                  actions={
                    <SpaceBetween size={'s'} direction='horizontal'>
                      <Button variant='link' onClick={closeModal}>
                        No
                      </Button>
                      <Button variant='primary' loading={buttonLoading} onClick={handleRelinquishPermission}>
                        Yes
                      </Button>
                    </SpaceBetween>
                  }
                />
              }
            >
              <div>
                Do you want to remove permissions for datasets below?
                {selected.map(({ id }) => (
                  <li key={id}>{id}</li>
                ))}
              </div>
            </Modal>
          </>
        }
        selectionType={(props.extraFeatures || props.multiSelection) && props.allowListed ? 'multi' : undefined}
        isItemDisabled={(item) => {
          if (!props.allowListed) return false;
          if (props.multiSelection) return;
          return (
            item['supportedAccessTypes'] == null ||
            // Currently disabling all IAM bulk permission requests
            !item['supportedAccessTypes'].includes('IAM') ||
            // Not allowed to re-add if already in cart
            props.cartItemIds.includes(item['id']) ||
            // Not allowed to add tables that are pending or already approved
            (roleProperties !== undefined &&
              (roleProperties.approvedPermissions.includes(item['id']) ||
                roleProperties.pendingPermissions.includes(item['id'])))
          );
        }}
        loading={isLoading}
        filter={
          (props.extraFeatures || props.multiSelection) && (
            <PropertyFilter
              {...propertyFilterProps}
              disabled={isSearchEnabled()}
              i18nStrings={getHCResourcePagePropertyFilter()}
              countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
            />
          )
        }
        pagination={
          (props.extraFeatures || props.multiSelection) && (
            <Pagination
              {...paginationProps}
              ariaLabels={paginationLabels}
              onChange={(e) => handlePageChange(e)}
              // currentPageIndex={currentPageIndex}
              openEnd={!allDataLoaded}
              disabled={paginationDisabled}
            />
          )
        }
        preferences={
          (props.extraFeatures || props.multiSelection) && (
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={largePageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          )
        }
      />
    </>
  );
};

export default BrowseTable;
